import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  Chip,
  ListItemAvatar,
  Avatar,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import { Client } from "@microsoft/microsoft-graph-client";
import { AccountInfo, InteractionRequiredAuthError, IPublicClientApplication } from "@azure/msal-browser";

// Simplified Owner interface matching the usage in AppTester
export interface Owner {
  id: string;
  displayName?: string;
  userPrincipalName?: string;
}

// ResolvedOwner includes a friendlyName for display
export interface ResolvedOwner extends Owner {
  friendlyName: string;
}

interface OwnersProps {
  appId: string;                        // e.g. pass in app.id from the parent
  instance: IPublicClientApplication;   // MSAL instance from parent
  accounts: AccountInfo[];             // the signed-in user accounts from parent
}

const Owners: React.FC<OwnersProps> = ({
  appId,
  instance,
  accounts,
}) => {
  const [owners, setOwners] = useState<Owner[]>([]);
  const [resolvedOwners, setResolvedOwners] = useState<ResolvedOwner[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [ownersExpanded, setOwnersExpanded] = useState(true);

  // 1) Fetch the /owners for this application
  useEffect(() => {
    if (!appId) return;

    const fetchOwners = async () => {
      setLoading(true);
      try {
        const response = await instance.acquireTokenSilent({
          scopes: ["Application.Read.All"], // Changed to read-only scope
          account: accounts[0],
        });
        const client = Client.init({
          authProvider: (done) => done(null, response.accessToken),
        });

        const ownersData = await client.api(`/applications/${appId}/owners`).get();
        setOwners(ownersData.value || []);
      } catch (error) {
        console.error("Failed to fetch owners:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOwners();
  }, [appId, instance, accounts]);

  // 2) Resolve friendlyName for each owner
  useEffect(() => {
    const resolveOwnerNames = async () => {
      if (owners.length === 0) {
        setResolvedOwners([]);
        return;
      }
      try {
        const response = await instance.acquireTokenSilent({
          scopes: ["Directory.Read.All"], // Read-only scope for directory access
          account: accounts[0],
        });
        const client = Client.init({
          authProvider: (done) => done(null, response.accessToken),
        });

        const resolved: ResolvedOwner[] = [];
        for (const owner of owners) {
          try {
            const ownerData = await client.api(`/directoryObjects/${owner.id}`).get();
            const friendlyName =
              ownerData.displayName ||
              ownerData.userPrincipalName ||
              ownerData.appDisplayName ||
              owner.id;

            resolved.push({ 
              ...owner, 
              friendlyName,
              userPrincipalName: ownerData.userPrincipalName || null,
              displayName: ownerData.displayName || null
            });
          } catch (e) {
            console.error("Error resolving owner name:", e);
            resolved.push({ ...owner, friendlyName: owner.id });
          }
        }
        setResolvedOwners(resolved);
      } catch (error) {
        console.error("Error resolving owner names:", error);
      }
    };

    resolveOwnerNames();
  }, [owners, instance, accounts]);

  const handleOwnersAccordionChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setOwnersExpanded(isExpanded);
  };

  return (
    <Card variant="outlined" sx={{ mb: 4 }}>
      <CardContent>
        {/* OWNERS SECTION - Updated with indigo theme like other read-only components */}
        <Accordion 
          expanded={ownersExpanded}
          onChange={handleOwnersAccordionChange}
          sx={{
            border: '1px solid #e0e7ff', // Indigo border like other components
            borderRadius: '8px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            mb: 2
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              background: 'linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)', // Indigo gradient
              borderBottom: ownersExpanded ? '1px solid #e0e7ff' : 'none',
              minHeight: '64px'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: '#3f51b5', // Indigo background
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2
              }}>
                <SecurityIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  color: "#1a237e", // Indigo text color
                  letterSpacing: "0.02em",
                  flexGrow: 1
                }}
              >
                Application Owners
              </Typography>
              <Chip 
                size="small"
                label={`${resolvedOwners.length} owners`}
                sx={{ 
                  mr: 1,
                  backgroundColor: '#e3f2fd',
                  color: '#1565c0',
                  fontWeight: 500,
                  border: '1px solid #bbdefb'
                }}
              />
              <Chip 
                size="small"
                label="Read-Only" 
                sx={{ 
                  backgroundColor: '#eeeeee',
                  color: '#424242',
                  fontWeight: 500,
                  border: '1px solid #cccccc'
                }}
              />
            </Box>
          </AccordionSummary>
          
          <AccordionDetails sx={{ 
            p: 0,
            backgroundColor: "#fafbff" // Light indigo background
          }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            )}
            
            {!loading && resolvedOwners.length === 0 ? (
              <Box sx={{ 
                p: 4, 
                m: 2,
                backgroundColor: '#f8f9fa', 
                borderRadius: '12px', 
                textAlign: 'center',
                border: '2px dashed #dee2e6'
              }}>
                <Box sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  backgroundColor: '#e9ecef',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}>
                  <SecurityIcon sx={{ fontSize: '2rem', color: '#6c757d' }} />
                </Box>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  No owners configured
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This application does not have any owners configured.
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 2 }}>
                {resolvedOwners.map((owner, index) => (
                  <ListItem
                    key={owner.id}
                    sx={{
                      mb: 2,
                      p: 2,
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                      backgroundColor: 'white',
                      borderLeft: '4px solid #3f51b5' // Indigo left border
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: '#e8eaf6' }}>
                        <PersonIcon sx={{ color: '#3f51b5' }} />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={
                        <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                          {owner.friendlyName}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          {owner.userPrincipalName ? (
                            <Typography variant="body2" color="text.secondary">
                              <strong>UPN:</strong> {owner.userPrincipalName}
                            </Typography>
                          ) : (
                            <Chip 
                              label="Service Principal" 
                              size="small" 
                              sx={{
                                height: 20, 
                                fontSize: '0.7rem',
                                backgroundColor: '#e8eaf6',
                                color: '#3f51b5',
                                border: '1px solid #c5cae9'
                              }}
                            />
                          )}
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                            <strong>Object ID:</strong> {owner.id}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}

            {/* About Application Owners */}
            <Box sx={{
              m: 2,
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)',
              opacity: 0.85
            }}>
              <Accordion sx={{ '&:before': { display: 'none' }, borderRadius: '8px !important' }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.2rem', color: '#9e9e9e' }} />}
                  sx={{
                    background: '#f8f9fa',
                    borderBottom: '1px solid #e9ecef',
                    minHeight: '48px',
                    '&:hover': { backgroundColor: '#f1f3f4' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <InfoOutlinedIcon sx={{ mr: 1.5, fontSize: "1.1rem", color: 'info.main' }} />
                    <Typography variant="subtitle1" sx={{ fontSize: "1rem", fontWeight: 500 }}>
                      About Application Owners
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ p: 2, backgroundColor: "#f5f5ff", borderRadius: 1 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Application owners are users who have administrative access to manage an application registration in Azure Active Directory.
                    </Typography>
                    
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: '#ffffff', 
                      border: '1px solid #e0e0ff', 
                      borderRadius: 1,
                      mb: 2
                    }}>
                      <Typography variant="subtitle2" sx={{ color: '#0d47a1', mb: 1 }}>
                        What Owners Can Do:
                      </Typography>
                      <Typography variant="body2">
                        • <strong>Update application attributes:</strong> Modify name, reply URLs, credentials, etc.<br />
                        • <strong>Manage permissions:</strong> Add or remove API permissions<br />
                        • <strong>Configure authentication:</strong> Control sign-in settings and token configuration<br />
                        • <strong>Add other owners:</strong> Share management responsibilities with other users<br />
                        • <strong>Delete the application:</strong> Remove the entire application registration
                      </Typography>
                    </Box>
                    
                    <Box sx={{
                      p: 2,
                      backgroundColor: '#fff8e1',
                      border: '1px solid #ffe082',
                      borderRadius: 1
                    }}>
                      <Typography variant="subtitle2" sx={{ color: '#bf360c', mb: 1 }}>
                        Important Considerations:
                      </Typography>
                      <Typography variant="body2">
                        • Every application should have at least one owner<br />
                        • Only add owners who need administrative access to the application<br />
                        • Owners are not automatically granted access to use the application<br />
                        • Users must exist in your Entra ID tenant before they can be added as owners<br />
                        • Owner management is typically done through the Azure Portal under App Registrations
                      </Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default Owners;